import { useState } from "react";
import "./measurement.css";

const lengthUnits = {
  mm: 0.1,
  cm: 1,
  m: 100,
  inch: 2.54,
  feet: 30.48,
  yard: 91.44,
  mile: 160934,
  "nautical mile": 185200,
};

const unitFactorsToMeter = {
  Feet: 0.3048,
  Inch: 0.0254,
  Meter: 1,
  Cm: 0.01,
};

const weightUnits = {
  Gram: 1,
  Kilogram: 1000,
  Ton: 1000000,
  Ounce: 28.3495,
  Pound: 453.592,
};

const liquidUnits = {
  Litre: 1,
  "Fluid Ounces": 0.0295735,
  Quart: 0.946353,
  Gallon: 3.78541,
  "Imperial Gallon": 4.54609,
};

const volumeUnits = {
  "Cubic Mm": 0.001,
  "Cubic Cm": 1,
  "Cubic Inch": 16.3871,
  "Cubic Feet": 28316.8,
  "Cubic Meter": 1e6,
};

const Measurement = () => {
  const [category, setCategory] = useState("length");

  const [lengthValues, setLengthValues] = useState(
    Object.keys(lengthUnits).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );

  const [weightValues, setWeightValues] = useState(
    Object.keys(weightUnits).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );

  const [liquidValues, setLiquidValues] = useState(
    Object.keys(liquidUnits).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );

  const [selectedUnit, setSelectedUnit] = useState("");
  const [inputValue, setInputValue] = useState("");

  const [lengthInput, setLengthInput] = useState("");
  const [widthInput, setWidthInput] = useState("");
  const [heightInput, setHeightInput] = useState("");
  const [quantityInput, setQuantityInput] = useState("");
  const [cubicUnit, setCubicUnit] = useState("Feet");
  const [cubicMeterResult, setCubicMeterResult] = useState("");
  const [volumeWeightResult, setVolumeWeightResult] = useState("");

  const [volumeInputValue, setVolumeInputValue] = useState("");
  const [volumeInputUnit, setVolumeInputUnit] = useState("Cubic Cm");
  const [volumeOutputUnit, setVolumeOutputUnit] = useState("Cubic Meter");
  const [volumeResult, setVolumeResult] = useState("");

  const resetCommon = () => {
    setInputValue("");
    setSelectedUnit("");
  };

  const calculateLength = () => {
    if (!selectedUnit || inputValue === "") return;
    const cmValue = parseFloat(inputValue) * lengthUnits[selectedUnit];
    const updated = {};
    for (const key in lengthUnits) {
      updated[key] = (cmValue / lengthUnits[key]).toFixed(4);
    }
    setLengthValues(updated);
  };

  const resetLength = () => {
    resetCommon();
    setLengthValues(
      Object.keys(lengthUnits).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
    );
  };

  const calculateWeight = () => {
    if (!selectedUnit || inputValue === "") return;
    const gramValue = parseFloat(inputValue) * weightUnits[selectedUnit];
    const updated = {};
    for (const key in weightUnits) {
      updated[key] = (gramValue / weightUnits[key]).toFixed(4);
    }
    setWeightValues(updated);
  };

  const resetWeight = () => {
    resetCommon();
    setWeightValues(
      Object.keys(weightUnits).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
    );
  };

  const calculateLiquid = () => {
    if (!selectedUnit || inputValue === "") return;
    const litreValue = parseFloat(inputValue) * liquidUnits[selectedUnit];
    const updated = {};
    for (const key in liquidUnits) {
      updated[key] = (litreValue / liquidUnits[key]).toFixed(4);
    }
    setLiquidValues(updated);
  };

  const resetLiquid = () => {
    resetCommon();
    setLiquidValues(
      Object.keys(liquidUnits).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
    );
  };

  const calculateCubicMeter = () => {
    const l = parseFloat(lengthInput);
    const w = parseFloat(widthInput);
    const h = parseFloat(heightInput);
    const q = parseFloat(quantityInput);

    if (isNaN(l) || isNaN(w) || isNaN(h) || isNaN(q)) {
      setCubicMeterResult("");
      setVolumeWeightResult("");
      return;
    }

    const factor = unitFactorsToMeter[cubicUnit];
    const volumeInCubicMeters = l * factor * (w * factor) * (h * factor) * q;
    const volWeight = volumeInCubicMeters * 167;

    setCubicMeterResult(volumeInCubicMeters.toFixed(3));
    setVolumeWeightResult(volWeight.toFixed(3));
  };

  const resetCubic = () => {
    setLengthInput("");
    setWidthInput("");
    setHeightInput("");
    setQuantityInput("");
    setCubicMeterResult("");
    setVolumeWeightResult("");
    setCubicUnit("Feet");
  };

  const calculateVolumeConversion = () => {
    if (volumeInputValue === "" || isNaN(volumeInputValue)) {
      setVolumeResult("");
      return;
    }

    const inputVal = parseFloat(volumeInputValue);
    const inCm = inputVal * volumeUnits[volumeInputUnit]; 
    const converted = inCm / volumeUnits[volumeOutputUnit];
    setVolumeResult(converted.toFixed(5));
  };

  const resetVolume = () => {
    setVolumeInputValue("");
    setVolumeInputUnit("Cubic Cm");
    setVolumeOutputUnit("Cubic Meter");
    setVolumeResult("");
  };

  return (
    <div className="converter-wrapper">
      <h1 className="title">Airsteam Logistics - Measurement Converter</h1>

      <div className="tabs">
        {["length", "cubic", "weight", "liquid", "volume"].map((cat) => (
          <button
            key={cat}
            onClick={() => {
              setCategory(cat);
              resetCommon();
            }}
            className={`tab ${category === cat ? "active" : ""}`}
          >
            {cat
              .replace("cubic", "Cubic Meter")
              .replace("liquid", "Liquid Volume")
              .replace("volume", "Volume")
              .replace(/^./, (s) => s.toUpperCase())}
          </button>
        ))}
      </div>

      {category === "length" && (
        <div className="converter-card">
          <div className="length-grid">
            {Object.keys(lengthUnits).map((unit) => (
              <div key={unit} className="unit-box">
                <label>{unit.toUpperCase()}</label>
                <input
                  type="number"
                  value={
                    selectedUnit === unit ? inputValue : lengthValues[unit]
                  }
                  onChange={(e) => {
                    setSelectedUnit(unit);
                    setInputValue(e.target.value);
                  }}
                />
              </div>
            ))}
          </div>
          <div className="button-group">
            <button className="calc-btn" onClick={calculateLength}>
              Calculate
            </button>
            <button className="reset-btn" onClick={resetLength}>
              Reset
            </button>
          </div>
        </div>
      )}

      {category === "cubic" && (
        <div className="converter-card">
          <div className="length-grid">
            <div className="unit-box">
              <label>Length</label>
              <input
                type="number"
                value={lengthInput}
                onChange={(e) => setLengthInput(e.target.value)}
              />
            </div>
            <div className="unit-box">
              <label>Width</label>
              <input
                type="number"
                value={widthInput}
                onChange={(e) => setWidthInput(e.target.value)}
              />
            </div>
            <div className="unit-box">
              <label>Height</label>
              <input
                type="number"
                value={heightInput}
                onChange={(e) => setHeightInput(e.target.value)}
              />
            </div>
            <div className="unit-box">
              <label>Quantity</label>
              <input
                type="number"
                value={quantityInput}
                onChange={(e) => setQuantityInput(e.target.value)}
              />
            </div>
            <div className="unit-box">
              <label>Unit</label>
              <select
                value={cubicUnit}
                onChange={(e) => setCubicUnit(e.target.value)}
                className="unit-select"
              >
                <option value="Feet">Feet</option>
                <option value="Inch">Inch</option>
                <option value="Meter">Meter</option>
                <option value="Cm">Centimeter</option>
              </select>
            </div>
            <div className="unit-box">
              <label>Cubic Meter</label>
              <input type="text" value={cubicMeterResult} readOnly />
            </div>
            <div className="unit-box">
              <label>Volume Weight</label>
              <input type="text" value={volumeWeightResult} readOnly />
            </div>
          </div>
          <div className="button-group">
            <button className="calc-btn" onClick={calculateCubicMeter}>
              Calculate
            </button>
            <button className="reset-btn" onClick={resetCubic}>
              Reset
            </button>
          </div>
        </div>
      )}

      {category === "weight" && (
        <div className="converter-card">
          <div className="length-grid">
            {Object.keys(weightUnits).map((unit) => (
              <div key={unit} className="unit-box">
                <label>{unit}</label>
                <input
                  type="number"
                  value={
                    selectedUnit === unit ? inputValue : weightValues[unit]
                  }
                  onChange={(e) => {
                    setSelectedUnit(unit);
                    setInputValue(e.target.value);
                  }}
                />
              </div>
            ))}
          </div>
          <div className="button-group">
            <button className="calc-btn" onClick={calculateWeight}>
              Calculate
            </button>
            <button className="reset-btn" onClick={resetWeight}>
              Reset
            </button>
          </div>
        </div>
      )}

      {category === "liquid" && (
        <div className="converter-card">
          <div className="length-grid">
            {Object.keys(liquidUnits).map((unit) => (
              <div key={unit} className="unit-box">
                <label>{unit}</label>
                <input
                  type="number"
                  value={
                    selectedUnit === unit ? inputValue : liquidValues[unit]
                  }
                  onChange={(e) => {
                    setSelectedUnit(unit);
                    setInputValue(e.target.value);
                  }}
                />
              </div>
            ))}
          </div>
          <div className="button-group">
            <button className="calc-btn" onClick={calculateLiquid}>
              Calculate
            </button>
            <button className="reset-btn" onClick={resetLiquid}>
              Reset
            </button>
          </div>
        </div>
      )}

      {category === "volume" && (
        <div className="converter-card">
          <div className="length-grid">
            <div className="unit-box">
              <label>Input Unit</label>
              <select
                value={volumeInputUnit}
                onChange={(e) => setVolumeInputUnit(e.target.value)}
                className="unit-select"
              >
                {Object.keys(volumeUnits).map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
            <div className="unit-box">
              <label>Value</label>
              <input
                type="number"
                value={volumeInputValue}
                onChange={(e) => setVolumeInputValue(e.target.value)}
              />
            </div>
            <div className="unit-box">
              <label>Output Unit</label>
              <select
                value={volumeOutputUnit}
                onChange={(e) => setVolumeOutputUnit(e.target.value)}
                className="unit-select"
              >
                {Object.keys(volumeUnits).map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
            <div className="unit-box">
              <label>Result</label>
              <input type="text" value={volumeResult} readOnly />
            </div>
          </div>
          <div className="button-group">
            <button className="calc-btn" onClick={calculateVolumeConversion}>
              Calculate
            </button>
            <button className="reset-btn" onClick={resetVolume}>
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Measurement;
